import { GridColDef } from '@mui/x-data-grid-pro';
import useSWR from 'swr';
import { formatPhoneNumber, getOptions, gridDateTimeFormatter } from '@shared/commonFunctions';
import { gridBooleanCell, dataGridCol } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColPhone } from '@shared/components/grid/GridColPhone';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { IPeriod } from '@shared/interfaces';
import { Wrapper } from '@styles/CommonStyles';
import { requestCreateUser, requestUpdateUser } from './queries';
import { usersValidationSchema } from './validation';
import { IUser, OfficeLocation } from './interfaces';

const newRowModel = {
  isActive: true,
  isDeleted: false,
};

export const Users = () => {
  const { data, mutate: reloadUsers } = useSWR<IUser[]>(`${SERVICES.USER}/users`);
  const { data: trainingPeriods = [] } = useSWR<IPeriod[]>(`${SERVICES.USER}/period`);
  const { data: officeLocations = [] } = useSWR<OfficeLocation[]>(`${SERVICES.USER}/officeLocation`);

  const columns: GridColDef[] = [
    dataGridCol('firstName', {
      renderEditCell: (params) => <GridColTextInput params={params} />,
    }),
    dataGridCol('lastName', {
      renderEditCell: (params) => <GridColTextInput params={params} />,
    }),
    dataGridCol('email', {
      flex: 2,
      renderEditCell: (params) => <GridColTextInput params={params} />,
    }),
    dataGridCol('altEmail', {
      flex: 2,
      headerName: 'Alternate Email',
      renderEditCell: (params) => <GridColTextInput params={params} />,
    }),
    dataGridCol('phoneNumber', {
      flex: 1.1,
      renderEditCell: (params) => <GridColPhone params={params} />,
      valueFormatter: ({ value }) => formatPhoneNumber(value),
    }),
    dataGridCol('phoneExtension', {
      flex: 1,
      headerName: 'Phone Ext',
      renderEditCell: (params) => (
        <GridColNumber
          removeThousandSeparator
          params={params}
        />
      ),
    }),
    dataGridCol('paySystemUserId', {
      renderEditCell: (params) => (
        <GridColNumber
          removeThousandSeparator
          params={params}
        />
      ),
    }),
    dataGridCol('terminationDate', {
      type: 'dateTime',
      minWidth: 150,
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('trainingPeriodId', {
      headerName: 'Training Period',
      renderCell: (params) => (
        <GridColSelect
          options={getOptions(trainingPeriods)}
          params={params}
        />
      ),
      renderEditCell: (params) => (
        <GridColSelect
          options={getOptions(trainingPeriods)}
          params={params}
        />
      ),
    }),
    dataGridCol('officeLocationId', {
      headerName: 'Office Location',
      renderCell: (params) => (
        <GridColSelect
          options={getOptions(officeLocations, 'id', 'location')}
          params={params}
        />
      ),
      renderEditCell: (params) => (
        <GridColSelect
          isSearchable
          canBeClean={false}
          options={getOptions(officeLocations, 'id', 'location')}
          params={params}
        />
      ),
    }),
    dataGridCol('isActive', {
      ...gridBooleanCell,
    }),
    dataGridCol('isDeleted', {
      headerName: 'Deleted',
      ...gridBooleanCell,
    }),
  ].map((column) => ({ editable: true, minWidth: 100, flex: 1, ...column }));

  const gridActions = {
    onSave: async (row: IUser) => {
      if (Object.prototype.hasOwnProperty.call(row, 'id')) {
        const response = row.id === 'temp' ? await requestCreateUser(row) : await requestUpdateUser(row);

        if (!response?.error) {
          reloadUsers();
        }
        return response;
      }
    },
  };

  return (
    <Wrapper>
      <PhoenixGrid
        autoHeight
        withHeader
        disableSelectionOnClick
        showClearAllFilter
        name="User"
        pageSize={13}
        columns={columns}
        loading={!data}
        rows={data || []}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        actions={gridActions}
        validateSchema={usersValidationSchema}
        newRowModel={newRowModel}
      />
    </Wrapper>
  );
};
